import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";
import { Check, Zap } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [method, setMethod] = useState("stripe");
  const [loading, setLoading] = useState("");
  const [roleBlocked, setRoleBlocked] = useState(false);

  const providers = plans?.providers || {
    stripe: { enabled: true, message: null },
    paypal: { enabled: true, message: null },
  };

  useEffect(() => {
    api.get("/pricing/plans").then((r) => {
      setPlans(r.data);
      const p = r.data?.providers;
      if (p && !p.stripe?.enabled && p.paypal?.enabled) setMethod("paypal");
      if (p && !p.paypal?.enabled) setMethod("stripe");
    });
  }, []);

  const checkout = async (packageId) => {
    if (!user) { navigate("/auth?mode=register&role=contractor"); return; }
    if (user.role !== "contractor") {
      setRoleBlocked(true);
      toast.error("Switch to a Pro account to buy leads.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setLoading(packageId);
    try {
      const origin = window.location.origin;
      const r = await api.post("/checkout/session", {
        package_id: packageId, origin_url: origin, provider: method,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("No checkout URL returned");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Checkout failed";
      if (e.response?.status === 403) {
        setRoleBlocked(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      toast.error(detail);
    } finally {
      setLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <Seo
        title="Pricing for Pros"
        path="/pricing"
        description="Pay per lead or subscribe monthly. Credit packs from $25, plans from $49/month, plus Featured Pro placement to win more work."
      />
      <Navbar />
      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
          <div className="overline mb-3">// Pricing</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl uppercase tracking-tighter">Pay your way.</h1>
          <p className="text-muted-foreground mt-4 max-w-xl">Buy credits and pay per lead, or subscribe for unlimited / quota-based lead access. No commissions on jobs you close.</p>

          {roleBlocked && (
            <div className="mt-8 border-2 border-ink bg-acid p-5 max-w-2xl" data-testid="role-blocked-banner">
              <div className="overline">// Pro account required</div>
              <p className="font-display font-black text-2xl uppercase tracking-tight mt-2">
                Switch to a Pro account to buy leads
              </p>
              <p className="text-sm mt-2">
                You're signed in as a {user?.role || "customer"}. Lead credits, subscriptions and boosts are for
                contractors (Pros) only.
              </p>
              <button
                type="button"
                onClick={() => navigate("/auth?mode=register&role=contractor")}
                className="brut-btn-primary mt-5"
                data-testid="upgrade-to-pro-btn"
              >
                Create a Pro account →
              </button>
            </div>
          )}

          <div className="mt-8 flex gap-0 border-2 border-ink max-w-sm">
            {providers.stripe.enabled && (
              <button onClick={() => setMethod("stripe")} className={`flex-1 py-3 font-mono uppercase text-xs tracking-widest font-bold ${method === "stripe" ? "bg-klein text-white" : "bg-white"}`} data-testid="pay-stripe">Stripe</button>
            )}
            {providers.paypal.enabled && (
              <button onClick={() => setMethod("paypal")} className={`flex-1 py-3 font-mono uppercase text-xs tracking-widest font-bold ${providers.stripe.enabled ? "border-l-2 border-ink" : ""} ${method === "paypal" ? "bg-klein text-white" : "bg-white"}`} data-testid="pay-paypal">PayPal</button>
            )}
            {!providers.stripe.enabled && !providers.paypal.enabled && (
              <div className="flex-1 py-3 px-4 font-mono uppercase text-xs tracking-widest font-bold bg-white text-muted-foreground" data-testid="no-providers-available">
                Payments temporarily unavailable
              </div>
            )}
          </div>
          {(!providers.stripe.enabled || !providers.paypal.enabled) && (
            <p className="mt-3 text-xs font-mono text-muted-foreground max-w-sm" data-testid="provider-disabled-note">
              {[providers.stripe.message, providers.paypal.message].filter(Boolean).join(" ")}
            </p>
          )}
        </div>
      </section>

      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
          <div className="overline mb-6">// Subscriptions · monthly</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-ink">
            {plans?.subscriptions?.map((p, i) => (
              <div key={p.id} className={`p-8 border-r-2 last:border-r-0 border-ink ${i === 1 ? "bg-ink text-white" : "bg-white"}`} data-testid={`plan-${p.id}`}>
                <div className={`overline ${i === 1 ? "text-acid" : ""}`}>{p.name}</div>
                <div className="font-display font-black text-5xl mt-4">${p.price.toFixed(0)}<span className="text-base font-mono">/mo</span></div>
                <div className="overline mt-2">{p.lead_quota >= 999 ? "Unlimited" : `${p.lead_quota} leads / month`}</div>
                <ul className="mt-6 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm"><Check className={`w-4 h-4 mt-0.5 ${i === 1 ? "text-acid" : ""}`} /> {f}</li>
                  ))}
                </ul>
                <button disabled={loading === `sub:${p.id}`} onClick={() => checkout(`sub:${p.id}`)} className={`mt-8 w-full ${i === 1 ? "brut-btn-acid" : "brut-btn-primary"}`} data-testid={`sub-${p.id}`}>
                  {loading === `sub:${p.id}` ? "Loading…" : "Subscribe"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
          <div className="overline mb-6">// Credit packs · pay per lead</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-ink">
            {plans?.credit_packs?.map((p) => (
              <div key={p.id} className="p-8 border-r-2 last:border-r-0 border-ink bg-white" data-testid={`pack-${p.id}`}>
                <div className="overline">Pack</div>
                <div className="font-display font-black text-5xl mt-4">${p.amount.toFixed(0)}</div>
                <div className="overline mt-2 bg-acid border-2 border-ink inline-block px-2">{p.credits} credits</div>
                <p className="text-sm text-muted-foreground mt-6">Use credits to unlock leads in any category. Credits never expire.</p>
                <button disabled={loading === `credits:${p.id}`} onClick={() => checkout(`credits:${p.id}`)} className="brut-btn-primary w-full mt-8" data-testid={`buy-${p.id}`}>
                  {loading === `credits:${p.id}` ? "Loading…" : <><Zap className="w-4 h-4 mr-1" /> Buy</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
          <div className="flex items-end gap-4 mb-6">
            <div>
              <div className="overline">// Featured Pro Boost</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mt-2">Get top placement</h2>
            </div>
            <span className="font-mono uppercase tracking-widest text-xs bg-acid border-2 border-ink px-3 py-1">For approved pros</span>
          </div>
          <p className="text-muted-foreground max-w-2xl mb-8">Featured Pros appear at the top of category listings and get a highlighted badge. Boosts stack with subscriptions and pay-per-lead.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-ink">
            {plans?.boosts?.map((p, i) => (
              <button
                key={p.id}
                type="button"
                disabled={loading === `boost:${p.id}`}
                onClick={() => checkout(`boost:${p.id}`)}
                className={`text-left p-8 border-r-2 last:border-r-0 border-ink transition-all hover:bg-ink hover:text-acid group ${i === 1 ? "bg-acid" : "bg-white"}`}
                data-testid={`boost-${p.id}`}
              >
                <div className="overline group-hover:text-acid">{p.name}</div>
                <div className="font-display font-black text-6xl mt-4 underline decoration-2 underline-offset-4 decoration-ink group-hover:decoration-acid" data-testid={`boost-price-${p.id}`}>
                  ${p.price.toFixed(0)}
                </div>
                <div className="overline mt-2 group-hover:text-acid" data-testid={`boost-duration-${p.id}`}>{p.duration_days} days</div>
                <p className="text-sm mt-6 opacity-80">{p.description}</p>
                <div className={`mt-8 inline-flex items-center font-mono uppercase tracking-widest text-xs font-bold border-2 px-4 py-2 ${i === 1 ? "border-ink bg-ink text-acid group-hover:bg-acid group-hover:text-ink group-hover:border-acid" : "border-ink bg-acid text-ink group-hover:bg-acid"}`} data-testid={`buy-boost-${p.id}`}>
                  {loading === `boost:${p.id}` ? "Loading…" : `Activate — $${p.price.toFixed(0)} →`}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
